import {
  ChevronDownIcon,
  MenuIcon,
  XIcon,
  LogOutIcon,
} from "../../../utils/navbar-icons";
import type { useNavbar } from "../../../hooks/common/useNavbar";
import { Link } from "react-router-dom";
import { Avatar } from "../ui/avatar";

interface MobileNavbarProps {
  logoSrc: string;
  gymName: string | null;
  email: string | null;
  isSuperAdmin: boolean;
  isMenuOpen: boolean;
  openDropdown: string | null;
  isDarkMode: boolean;
  toggleTheme: () => void;
  superAdminMenuItems: ReturnType<typeof useNavbar>["superAdminMenuItems"];
  menuCategories: ReturnType<typeof useNavbar>["menuCategories"];
  mobileMenuRef: ReturnType<typeof useNavbar>["mobileMenuRef"];
  dropdownRefs: ReturnType<typeof useNavbar>["dropdownRefs"];
  toggleDropdown: ReturnType<typeof useNavbar>["toggleDropdown"];
  handleMobileMenuToggle: ReturnType<
    typeof useNavbar
  >["handleMobileMenuToggle"];
  closeMobileMenu: ReturnType<typeof useNavbar>["closeMobileMenu"];
  logout: ReturnType<typeof useNavbar>["logout"];
}

export const MobileNavbar = ({
  logoSrc,
  gymName,
  email,
  isSuperAdmin,
  isMenuOpen,
  openDropdown,
  isDarkMode,
  toggleTheme,
  superAdminMenuItems,
  menuCategories,
  mobileMenuRef,
  dropdownRefs,
  toggleDropdown,
  handleMobileMenuToggle,
  closeMobileMenu,
  logout,
}: MobileNavbarProps) => {
  return (
    <div className="lg:hidden relative">
      <div
        className="flex items-center justify-between"
        style={{ height: "var(--height-navbar-mobile)" }}
      >
        {!isSuperAdmin && (
          <Link to="/" className="flex items-center gap-1.5 min-w-0 flex-1">
            <span className="w-7 h-7 block shrink-0 rounded-md overflow-hidden">
              <Avatar
                src={logoSrc}
                alt={gymName ?? "Gym"}
                fallback={
                  logoSrc
                    ? (gymName?.slice(0, 2).toUpperCase() ?? "G")
                    : "Sin imagen"
                }
                className="w-full h-full"
              />
            </span>
            <span className="text-global-title font-bold text-size-xs tracking-tight truncate">
              {gymName?.toUpperCase() || ""}
            </span>
          </Link>
        )}
        {isSuperAdmin && (
          <Link
            to="/monitoring"
            className="flex items-center gap-1.5 min-w-0 shrink-0"
          >
            <span className="w-7 h-7 block shrink-0 rounded-md overflow-hidden">
              <Avatar
                src={logoSrc}
                alt="Superadmin"
                fallback={
                  logoSrc
                    ? "Superadmin".slice(0, 1).toUpperCase()
                    : "Sin imagen"
                }
                className="w-full h-full"
              />
            </span>
            <span className="text-global-title font-bold text-size-xs tracking-tight truncate">
              Superadmin
            </span>
          </Link>
        )}

        <div className="flex items-center gap-1 shrink-0 ml-2">
          <button
            type="button"
            onClick={toggleTheme}
            className="w-9 h-9 flex items-center justify-center text-global-tertiary hover:text-global-primary rounded-md transition-colors"
            aria-label={
              isDarkMode ? "Cambiar a modo claro" : "Cambiar a modo oscuro"
            }
          >
            {isDarkMode ? (
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M12 7a5 5 0 100 10A5 5 0 0012 7z"
                />
              </svg>
            ) : (
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                />
              </svg>
            )}
          </button>
          <button
            type="button"
            onClick={handleMobileMenuToggle}
            className="w-9 h-9 flex items-center justify-center text-global-tertiary hover:text-global-primary rounded-md transition-colors"
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? (
              <XIcon className="w-5 h-5" />
            ) : (
              <MenuIcon className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {isMenuOpen && (
        <div
          ref={mobileMenuRef}
          data-mobile-menu
          className="absolute left-0 right-0 top-full bg-global-primary border-t border-global-primary shadow-global-card pb-3 max-h-[calc(100vh-var(--height-navbar-mobile)-var(--height-env-banner))] overflow-y-auto z-50"
        >
          <div className="pt-2 px-3 pb-2 border-b border-global-primary">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                {gymName && !isSuperAdmin && (
                  <span className="px-2 py-0.5 text-size-2xs font-medium text-global-title-accent bg-global-accent-overlay rounded">
                    {gymName}
                  </span>
                )}
                {email && (
                  <span className="text-size-2xs text-global-secondary truncate">
                    {email}
                  </span>
                )}
              </div>
              <span className="text-size-2xs text-global-tertiary whitespace-nowrap">
                {new Date().toLocaleDateString("es-ES", {
                  day: "numeric",
                  month: "short",
                })}
              </span>
            </div>
          </div>

          <div className="pt-1.5 space-y-0.5 px-1">
            {isSuperAdmin
              ? superAdminMenuItems.map((item) => {
                  const isOpen = openDropdown === item.id;

                  if (item.hasSubItems) {
                    return (
                      <div
                        key={item.id}
                        ref={(el) => {
                          dropdownRefs.current[item.id] = el;
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => toggleDropdown(item.id)}
                          className="w-full flex items-center justify-between px-2.5 py-2 text-size-xs font-medium text-global-primary hover:bg-global-overlay-secondary active:bg-global-overlay-secondary rounded-md transition-colors"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {item.icon && (
                              <span className="shrink-0">{item.icon}</span>
                            )}
                            <span className="truncate">{item.label}</span>
                          </div>
                          <ChevronDownIcon
                            className={`w-3.5 h-3.5 transition-transform duration-200 shrink-0 ${isOpen ? "rotate-180" : ""}`}
                          />
                        </button>

                        {isOpen && item.subItems && (
                          <div className="pl-4 pr-2 py-0.5 space-y-0.5">
                            {item.subItems.map((subItem) => (
                              <Link
                                key={subItem.href}
                                to={subItem.href}
                                onClick={closeMobileMenu}
                                className="block px-2.5 py-1.5 text-size-xs text-global-secondary hover:text-global-primary hover:bg-global-overlay-secondary active:bg-global-overlay-secondary rounded-md transition-colors"
                              >
                                {subItem.label}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <Link
                      key={item.id}
                      to={item.href}
                      onClick={closeMobileMenu}
                      className="block px-2.5 py-2 text-size-xs font-medium text-global-primary hover:bg-global-overlay-secondary active:bg-global-overlay-secondary rounded-md transition-colors"
                    >
                      {item.label}
                    </Link>
                  );
                })
              : menuCategories.map((category) => {
                  const isOpen = openDropdown === category.id;
                  return (
                    <div
                      key={category.id}
                      ref={(el) => {
                        dropdownRefs.current[category.id] = el;
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => toggleDropdown(category.id)}
                        className="w-full flex items-center justify-between px-2.5 py-2 text-size-xs font-medium text-global-primary hover:bg-global-overlay-secondary active:bg-global-overlay-secondary rounded-md transition-colors"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="shrink-0">{category.icon}</span>
                          <span className="truncate">{category.label}</span>
                        </div>
                        <ChevronDownIcon
                          className={`w-3.5 h-3.5 transition-transform duration-200 shrink-0 ${isOpen ? "rotate-180" : ""}`}
                        />
                      </button>

                      {isOpen && (
                        <div className="pl-4 pr-2 py-0.5 space-y-0.5">
                          {category.items.map((item) => (
                            <Link
                              key={item.href}
                              to={item.href}
                              onClick={closeMobileMenu}
                              className="block px-2.5 py-1.5 text-size-xs text-global-secondary hover:text-global-primary hover:bg-global-overlay-secondary active:bg-global-overlay-secondary rounded-md transition-colors"
                            >
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
          </div>

          <div className="mt-2 px-2">
            <button
              type="button"
              onClick={logout}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-size-xs font-medium text-global-error hover:bg-global-error/10 active:bg-global-error/20 rounded-md transition-colors"
            >
              <LogOutIcon className="w-3.5 h-3.5" />
              Cerrar sesión
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
